import { t } from 'i18next';
import { Controller } from 'react-hook-form';
import {
  Autocomplete,
  Box,
  Button,
  InputAdornment,
  TextField,
} from '@mui/material';
import { Check, Search } from '@mui/icons-material';
import { SelectFormField, StatusBanner, TextFormField } from '@components';
import { useMedicareInsuranceEntry } from './hooks/useMedicareInsuranceEntry';

const medicareTypeOptions = [
  { value: 'commercial', label: 'Commercial' },
  { value: 'medicaid', label: 'Medicaid' },
  { value: 'medicare', label: 'Medicare' },
  { value: 'bcbs', label: 'BCBS' },
  { value: 'champus', label: 'Champus' },
  { value: 'workerscomp', label: 'Workers Comp' },
];

const insuranceTypeOptions = [
  { value: 'insurance', label: 'Insurance' },
  { value: 'cash_community', label: 'Cash community' },
  { value: 'cash_poa', label: 'Cash POA' },
  { value: 'cash_self', label: 'Cash self' },
];

interface IMedicareInsuranceEntryProps {
  patient_id: string;
}

export const MedicareInsuranceEntry = ({
  patient_id,
}: IMedicareInsuranceEntryProps) => {
  const {
    control,
    payers,
    payersQuery,
    setPayersQuery,
    verified,
    isCheckStatusPending,
    handleCheckInsuranceStatus,
  } = useMedicareInsuranceEntry({ patient_id });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {!verified && (
        <StatusBanner
          text={t('patient.detailsCard.editPrimaryInsurance.banner.unverified')}
          variant='warning'
        />
      )}
      <Controller
        name='entry.payer_name'
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Autocomplete
            disablePortal
            options={payers}
            noOptionsText={t(
              `patient.detailsCard.editPrimaryInsurance.${payersQuery.length > 1 ? 'noOptions' : 'typeTwoLetters'}`,
            )}
            getOptionLabel={(option: { id: string; name: string }) =>
              option.name
            }
            filterOptions={options => options}
            onChange={(_, newValue) => {
              onChange(newValue);
              setPayersQuery(newValue?.name || '');
            }}
            value={value ?? null}
            renderInput={params => (
              <TextField
                {...params}
                required
                value={payersQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPayersQuery(e.target.value)
                }
                label={t('patient.detailsCard.editPrimaryInsurance.payerName')}
                error={!!error}
                helperText={error?.message}
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...params.slotProps.input,
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Search fontSize='small' />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
        )}
      />

      <SelectFormField
        name='entry.payer_type'
        label={t('patient.detailsCard.editPrimaryInsurance.payerType')}
        options={medicareTypeOptions}
        optional={false}
      />

      <Box sx={{ position: 'relative' }}>
        <TextFormField
          name='entry.mbi'
          label='Medicare Beneficiary Identifier (MBI)'
          optional={false}
        />
        <Button
          sx={{
            position: 'absolute',
            top: '14px',
            right: '12px',
            height: '30px',
          }}
          variant='outlined'
          color={verified ? 'success' : 'primary'}
          startIcon={verified ? <Check color='success' /> : null}
          disabled={isCheckStatusPending}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();
            handleCheckInsuranceStatus();
          }}
        >
          {t(
            `patient.detailsCard.editPrimaryInsurance.${verified ? 'verified' : 'verify'}`,
          )}
        </Button>
      </Box>

      <SelectFormField
        name='entry.payment_type'
        label='Payment type'
        options={insuranceTypeOptions}
        optional={false}
      />
    </Box>
  );
};
